import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Alert,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Customer-side cancel confirmation. Optional reason textarea.
 * The actual mutation lives in useCancelCustomerOrder; this dialog
 * just collects the input and calls onConfirm(reason).
 */
const CancelOrderDialog = ({ open, order, onClose, onConfirm, isSubmitting }) => {
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {t('orders.cancelTitle', 'Cancel order')}{order?.number ? ` #${order.number}` : ''}
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t(
                        'orders.cancelWarning',
                        "This cannot be undone; stock will be returned. You'll receive a cancellation email shortly."
                    )}
                </Alert>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={t('orders.cancelReason', 'Reason (optional)')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={isArabic ? 'مثال: طلبت بالخطأ' : 'e.g. ordered by mistake'}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t(
                        'orders.cancelReasonHint',
                        'Helps us improve. Not shown to anyone except staff.'
                    )}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                    {t('common.back', 'Back')}
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    onClick={() => onConfirm(reason.trim())}
                    disabled={isSubmitting}
                >
                    {isSubmitting
                        ? t('common.working', 'Working...')
                        : t('orders.cancelConfirm', 'Cancel order')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CancelOrderDialog;
